/**
 * Right-side overlay panel for create/edit forms on management pages.
 */

import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/** Props for {@link SlidePanel}. */
interface SlidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  slideDirection?: 'left' | 'right';
}

/** Side panel overlay for details or forms. */
export const SlidePanel: React.FC<SlidePanelProps> = ({
  isOpen,
  onClose,
  children,
  slideDirection = 'right',
}) => {
  const [shouldRender, setShouldRender] = React.useState(isOpen);

  /** Keep the panel mounted for 300ms after close so the exit animation can finish. */
  React.useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    } else {
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!shouldRender) {
    return null;
  }

  // Portal to document.body: escapes any ancestor CSS transform / stacking context
  // (e.g. DashboardLayout uses transform:scale which breaks position:fixed otherwise)
  return createPortal(
    <>
      <button
        className="slide-panel-backdrop"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        aria-label="Fermer le panneau"
        type="button"
        style={{
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 200ms var(--ease-out)',
        }}
      />
      <div
        className={cn(
          'slide-panel',
          slideDirection === 'left' ? 'slide-panel-left' : 'slide-panel-right',
          isOpen && 'open',
        )}
      >
        <div className="slide-panel-content">{children}</div>
      </div>
    </>,
    document.body,
  );
};
